// Slider (DESIGN §3.6.11): Radix Slider. Spur 4 `progress-track`, Füllung `accent`, Daumen 20 `fg`
// mit `shadow-2`, Wert rechts in `kpi` `tabular-nums`. Einfach oder Bereich; Tastatur: Pfeile ±1
// Schritt, PageUp/PageDown ±10, Home/End.
import type { Ref } from 'react';
import { Slider as SliderPrimitive } from 'radix-ui';
import { useFieldProps } from './_lib/fieldContext';
import { cn } from '../basis/kern';

export interface SliderProps {
  ref?: Ref<HTMLSpanElement>;
  /** Ein Wert = einfach, zwei Werte = Bereich. */
  value?: number[];
  defaultValue?: number[];
  onValueChange?: (value: number[]) => void;
  onValueCommit?: (value: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  /** Anzeige des Werts rechts; `false` blendet sie aus. */
  formatValue?: ((value: number[]) => string) | false;
  /** Namen der Daumen (Bereich: „Minimum“, „Maximum“). */
  thumbLabels?: string[];
  'aria-label'?: string;
  className?: string;
}

const defaultFormat = (value: number[]) =>
  value.map((v) => new Intl.NumberFormat('en-GB').format(v)).join(' – ');

export function Slider({
  ref,
  formatValue = defaultFormat,
  thumbLabels,
  className,
  min = 0,
  max = 100,
  step = 1,
  ...rest
}: SliderProps) {
  const field = useFieldProps({});
  const current = rest.value ?? rest.defaultValue ?? [min];
  return (
    <div className={cn('flex w-full items-center gap-4', className)}>
      <SliderPrimitive.Root
        {...rest}
        ref={ref}
        min={min}
        max={max}
        step={step}
        minStepsBetweenThumbs={1}
        className="relative flex h-10 flex-1 touch-none items-center select-none data-[disabled]:opacity-50"
      >
        <SliderPrimitive.Track className="relative h-1 grow rounded-full bg-progress-track">
          <SliderPrimitive.Range className="absolute h-full rounded-full bg-accent" />
        </SliderPrimitive.Track>
        {current.map((_, i) => (
          <SliderPrimitive.Thumb
            key={i}
            aria-label={
              thumbLabels?.[i] ??
              (current.length > 1 ? (i === 0 ? 'Minimum' : 'Maximum') : rest['aria-label'])
            }
            aria-labelledby={field.id && !rest['aria-label'] ? `${field.id}-label` : undefined}
            aria-describedby={field['aria-describedby']}
            className="block size-5 rounded-full bg-fg shadow-2 transition-transform duration-(--bar-dur-instant) hover:scale-110 data-[disabled]:cursor-not-allowed"
          />
        ))}
      </SliderPrimitive.Root>
      {formatValue ? (
        <output aria-hidden="true" className="min-w-12 text-right text-kpi text-fg tabular-nums">
          {formatValue(current)}
        </output>
      ) : null}
    </div>
  );
}
